<script setup lang="ts">
/**
 * Radio button Source - SOURCE UI KIT Interne (Forms › Radio Button, node 7381:9842).
 * Anatomie Figma : pastille circulaire + label (+ helper via SourceHint si hasHelper / isOnError).
 * Pas de variante card.
 */
import {computed, useId} from 'vue'

import SourceHint from '~/components/ui/SourceHint.vue'

type Size = 'small' | 'medium'
/** `onBlue` = contraste sur fond primary (section bleue de la maquette). */
type Theme = 'default' | 'onBlue'

type Props = {
  /** Valeur sélectionnée du groupe. */
  modelValue: string
  /** Valeur de cette option. */
  value: string
  label: string
  /**
   * Helper sous le composant (hasHelper Figma).
   * Obligatoire d’affichage quand `isOnError` - expliquer la correction attendue.
   */
  helper?: string
  /** Alias de `helper` (rétrocompat catalogue / usages). */
  description?: string
  disabled?: boolean
  /** Propriété Figma `isOnError` : anneau rouge + helper en erreur. */
  isOnError?: boolean
  /** Défaut Figma = small. */
  size?: Size
  theme?: Theme
  name?: string
}

const props = withDefaults(defineProps<Props>(), {
  helper: '',
  description: '',
  disabled: false,
  isOnError: false,
  size: 'small',
  theme: 'default',
  name: undefined,
})

const emit = defineEmits<{
  (event: 'update:modelValue', value: string): void
}>()

const uid = useId()
const hintId = computed(() => `${uid}-hint`)

const isSelected = computed(() => props.modelValue === props.value)

const helperText = computed(() => {
  return (props.helper || props.description || '').trim()
})

const showHint = computed(() => Boolean(helperText.value))

const hintState = computed(() => (props.isOnError ? ('error' as const) : ('default' as const)))

function select(): void {
  if (props.disabled || isSelected.value) {
    return
  }
  emit('update:modelValue', props.value)
}

function onKeydown(keyboardEvent: KeyboardEvent): void {
  if (keyboardEvent.key === ' ' || keyboardEvent.key === 'Enter') {
    keyboardEvent.preventDefault()
    select()
  }
}
</script>

<template>
  <div
    class="source-radio-wrap"
    :class="[
      size,
      `theme-${theme}`,
      { 'is-checked': isSelected, 'is-disabled': disabled, 'is-on-error': isOnError },
    ]"
  >
    <button
      type="button"
      role="radio"
      class="source-radio"
      :class="[
        size,
        `theme-${theme}`,
        { 'is-checked': isSelected, 'is-disabled': disabled, 'is-on-error': isOnError },
      ]"
      :aria-checked="isSelected"
      :aria-disabled="disabled || undefined"
      :aria-invalid="isOnError || undefined"
      :aria-describedby="showHint ? hintId : undefined"
      :disabled="disabled"
      :name="name"
      @click="select"
      @keydown="onKeydown"
    >
      <span class="source-radio__control" aria-hidden="true">
        <span class="source-radio__dot" />
      </span>
      <span class="source-radio__label">{{ label }}</span>
    </button>

    <SourceHint
      v-if="showHint"
      :hint-id="hintId"
      :text="helperText"
      :state="hintState"
    />
  </div>
</template>

<style scoped lang="scss">
@use '~/assets/scss/partials/elpdl/colors' as c;
@use '~/assets/scss/partials/elpdl/spacing' as *;

// Tokens composant - alignés maquette SOURCE UI KIT Interne (Forms › Radio Button).
.source-radio-wrap {
  --radio-size: 20px;
  --radio-gap: 10px;

  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: $spacing-3xs; /* 4px, espace pastille/label et helper */
  width: auto;
  max-width: 100%;

  &.small {
    --radio-size: 16px;
    --radio-gap: #{$spacing-2xs};
  }

  // Cas du helper Figma : aligné sous le label (décalage pastille + gap)
  :deep(.source-hint) {
    margin-left: calc(var(--radio-size) + var(--radio-gap));
  }
}

.source-radio {
  --radio-size: 20px;
  --radio-border-width: 2px;
  --radio-dot-size: 10px;
  --radio-gap: 10px;
  --radio-label-size: 1rem; /* body 16px (medium) */
  --radio-label-line: 1.25rem;
  --radio-border: #{c.$enedis-neutral-100};
  --radio-fill: #{c.$enedis-neutral-10};
  --radio-dot: #{c.$enedis-primary-500};
  --radio-label: #{c.$enedis-neutral-900};
  --radio-hover-border: #{c.$enedis-primary-500};
  --radio-hover-halo: #{c.$enedis-primary-200};
  --radio-focus-ring: #{c.$enedis-primary-400};
  --radio-checked-border: #{c.$enedis-primary-500};
  --radio-error-border: #{c.$enedis-error-500};
  --radio-error-hover: #{c.$enedis-error-400};
  --radio-error-halo: #{c.$enedis-error-200};
  --radio-disabled-border: #{c.$enedis-neutral-80};
  --radio-disabled-dot: #{c.$enedis-neutral-80};
  --radio-disabled-label: #{c.$enedis-neutral-100};

  display: inline-flex;
  align-items: center;
  gap: var(--radio-gap);
  margin: 0;
  padding: 0;
  border: none;
  background: transparent;
  cursor: pointer;
  text-align: left;
  font: inherit;
  color: inherit;

  &:focus-visible {
    outline: none;
  }

  &.is-disabled {
    cursor: not-allowed;
  }

  // Figma size=small (valeur par défaut du kit)
  &.small {
    --radio-size: 16px;
    --radio-border-width: 1.5px;
    --radio-dot-size: 8px;
    --radio-gap: #{$spacing-2xs}; /* 8px */
    --radio-label-size: 0.875rem; /* body small 14px */
    --radio-label-line: 1rem;
  }

  // Section bleue de la maquette - contraste blanc sur primary
  &.theme-onBlue {
    --radio-border: #{c.$enedis-neutral-10};
    --radio-fill: transparent;
    --radio-dot: #{c.$enedis-neutral-10};
    --radio-label: #{c.$enedis-neutral-10};
    --radio-hover-border: #{c.$enedis-neutral-10};
    --radio-hover-halo: rgb(255 255 255 / 20%);
    --radio-focus-ring: #{c.$enedis-neutral-10};
    --radio-checked-border: #{c.$enedis-neutral-10};
    --radio-error-border: #{c.$enedis-neutral-10};
    --radio-error-hover: #{c.$enedis-neutral-10};
    --radio-error-halo: rgb(255 255 255 / 20%);
    --radio-disabled-border: rgb(255 255 255 / 40%);
    --radio-disabled-dot: rgb(255 255 255 / 40%);
    --radio-disabled-label: rgb(255 255 255 / 40%);
  }
}

.source-radio__control {
  position: relative;
  box-sizing: border-box;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  width: var(--radio-size);
  height: var(--radio-size);
  border: var(--radio-border-width) solid var(--radio-border);
  border-radius: 50%;
  background: var(--radio-fill);
  // Halo circulaire hover (state=hover Figma) : box-shadow plutôt qu'un pseudo-élément
  // avec z-index négatif (invisible car passé derrière le fond des ancêtres opaques).
  box-shadow: 0 0 0 0 transparent;
  transition: border-color 0.15s ease, background-color 0.15s ease, box-shadow 0.15s ease;

  .source-radio:hover:not(.is-disabled):not(.is-on-error) & {
    border-color: var(--radio-hover-border);
    box-shadow: 0 0 0 6px var(--radio-hover-halo);
  }

  .source-radio.is-checked & {
    border-color: var(--radio-checked-border);
  }

  .source-radio.is-checked:hover:not(.is-disabled):not(.is-on-error) & {
    border-color: var(--radio-hover-border);
    box-shadow: 0 0 0 6px var(--radio-hover-halo);
  }

  // Focus Figma : anneau concentrique net (gap blanc) - pas un glow flou
  .source-radio:focus-visible:not(.is-disabled) & {
    border-color: var(--radio-checked-border);
    box-shadow:
      0 0 0 2px var(--radio-fill, #{c.$enedis-neutral-10}),
      0 0 0 4px var(--radio-focus-ring);
  }

  .source-radio.theme-onBlue:focus-visible:not(.is-disabled) & {
    box-shadow:
      0 0 0 2px c.$enedis-primary-500,
      0 0 0 4px var(--radio-focus-ring);
  }

  .source-radio.is-on-error & {
    border-color: var(--radio-error-border);
  }

  .source-radio.is-on-error:hover:not(.is-disabled) & {
    border-color: var(--radio-error-hover);
    box-shadow: 0 0 0 6px var(--radio-error-halo);
  }

  .source-radio.is-on-error:focus-visible:not(.is-disabled) & {
    border-color: var(--radio-error-border);
    box-shadow:
      0 0 0 2px var(--radio-fill, #{c.$enedis-neutral-10}),
      0 0 0 4px var(--radio-error-border);
  }

  .source-radio.is-disabled & {
    border-color: var(--radio-disabled-border);
    background: var(--radio-fill);
    box-shadow: 0 0 0 0 transparent;
  }
}

.source-radio__dot {
  width: var(--radio-dot-size);
  height: var(--radio-dot-size);
  border-radius: 50%;
  background: var(--radio-dot);
  opacity: 0;
  transform: scale(0.5);
  transition: opacity 0.15s ease, transform 0.15s ease, background-color 0.15s ease;

  .source-radio.is-checked & {
    opacity: 1;
    transform: scale(1);
  }

  .source-radio.is-checked.is-disabled & {
    background: var(--radio-disabled-dot);
  }
}

.source-radio__label {
  font-family: Public Sans, sans-serif;
  font-size: var(--radio-label-size);
  font-weight: 400;
  line-height: var(--radio-label-line);
  color: var(--radio-label);

  .source-radio.is-disabled & {
    color: var(--radio-disabled-label);
  }
}
</style>
